import type { FormContextType, RJSFSchema, StrictRJSFSchema, WidgetProps } from '@rjsf/utils';
import { ALT_TEXT_ENTRY_POINTS, AltTextWithGenAIField } from 'components/AltText/AltTextWithGenAIField';
import { useRequiredFieldOnChange } from '../utils/useRequiredFieldOnChange';

/**
 * RJSF widget that provides AI-powered alt text generation capabilities.
 *
 * Usage:
 * - Basic: Field named "iconImage.altText" will automatically reference "iconImage.src"
 * - Override: Use ui:options.imageFieldPath to specify a custom image field path
 */
export const AltTextWithAutoGenerate = <T = never, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = never>(
  props: WidgetProps<T, S, F>,
) => {
  const {
    label,
    uiSchema,
    id,
    registry: { formContext },
    value,
    onChange,
    onBlur,
    placeholder,
    disabled,
    rawErrors,
    required,
  } = props;

  const requiredFieldOnChange = useRequiredFieldOnChange(onChange, required);

  const handleBlur = () => {
    if (onBlur) {
      onBlur(id, value);
    }
  };

  return (
    <AltTextWithGenAIField
      disabled={disabled}
      entryPoint={ALT_TEXT_ENTRY_POINTS.DIRECT_PROPS}
      error={!!rawErrors?.length}
      fieldId={id}
      formContext={formContext}
      label={label}
      onBlur={handleBlur}
      onChange={requiredFieldOnChange}
      placeholder={placeholder}
      uiSchema={uiSchema}
      value={value || ''}
    />
  );
};
